/**
 * Opens a print-ready campaign flyer focused on scanning the QR code
 * to reach the take-action page. Organizers can Print or Save as PDF
 * from the browser print dialog.
 */

import { buildCampaignQrDataUrl } from "./campaignQrCode";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export class CampaignPrintFlyerError extends Error {
  constructor(message, code = "unknown") {
    super(message);
    this.name = "CampaignPrintFlyerError";
    this.code = code;
  }
}

export async function openCampaignPrintFlyer({
  name,
  description,
  actionScript,
  shareUrl,
  billLabel,
  organizerName,
}) {
  if (typeof window === "undefined") {
    throw new CampaignPrintFlyerError(
      "Printing is only available in a browser.",
      "unsupported"
    );
  }

  if (!shareUrl) {
    throw new CampaignPrintFlyerError(
      "This campaign does not have a share link yet.",
      "missing_share_url"
    );
  }

  const title = escapeHtml((name || "").trim() || "Take action");
  const bill = escapeHtml((billLabel || "").trim());
  const url = escapeHtml(shareUrl || "");
  const org = escapeHtml((organizerName || "").trim());
  const qrSrc = await buildCampaignQrDataUrl(shareUrl, 520);

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title} — Print flyer</title>
  <style>
    @page { size: letter; margin: 0.55in; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      height: 100%;
      min-height: 100vh;
      background: #fff;
      color: #0f172a;
      font-family: Georgia, "Times New Roman", serif;
    }
    body {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    .flyer {
      width: 100%;
      max-width: 7.5in;
      padding: 0.15in 0.1in;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      gap: 0.28in;
    }
    h1 {
      margin: 0;
      font-size: 34px;
      line-height: 1.15;
      font-weight: 700;
      color: #0f172a;
      max-width: 6.4in;
    }
    .qr-wrap {
      margin: 0.1in 0;
      padding: 0.18in;
      border: 2px solid #0f172a;
      border-radius: 8px;
      background: #fff;
    }
    .qr-wrap img {
      display: block;
      width: 3.35in;
      height: 3.35in;
    }
    .scan-cta {
      margin: 0;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 26px;
      font-weight: 800;
      color: #047857;
      letter-spacing: 0.01em;
    }
    .scan-sub {
      margin: 0;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 17px;
      line-height: 1.45;
      color: #0f172a;
      max-width: 5.5in;
    }
    .meta {
      width: 100%;
      max-width: 6.2in;
      text-align: left;
      border-top: 1px solid #cbd5e1;
      padding-top: 0.22in;
      display: grid;
      gap: 0.16in;
    }
    .meta h2 {
      margin: 0 0 0.06in;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #64748b;
    }
    .meta p {
      margin: 0;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 13px;
      line-height: 1.45;
      color: #0f172a;
      white-space: pre-wrap;
    }
    .url {
      margin: 0;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      word-break: break-all;
      color: #475569;
    }
    .footer-wrap {
      margin: 0.12in 0 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.06in;
    }
    .footer-organizer {
      margin: 0;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 14px;
      font-weight: 700;
      color: #0f172a;
    }
    .footer-brand {
      margin: 0;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      color: #64748b;
    }
    .screen-hint {
      display: none;
    }
    @media screen {
      body { background: #e2e8f0; padding: 24px; }
      .flyer {
        background: #fff;
        padding: 0.55in 0.45in;
        box-shadow: 0 8px 30px rgba(15, 23, 42, 0.12);
        border-radius: 8px;
      }
      .screen-hint {
        display: flex;
        flex-direction: column;
        gap: 10px;
        position: fixed;
        top: 16px;
        right: 16px;
        z-index: 10;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 13px;
        font-weight: 500;
        color: #334155;
        background: #fff;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 14px;
        max-width: 240px;
        line-height: 1.4;
        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      }
      .screen-hint button {
        background: #047857;
        color: #fff;
        border: none;
        padding: 10px;
        border-radius: 6px;
        font-weight: 700;
        font-size: 14px;
        cursor: pointer;
      }
      .screen-hint button:hover {
        background: #065f46;
      }
    }
    @media print {
      .screen-hint { display: none !important; }
      html, body {
        height: 100%;
        min-height: 100%;
        background: #fff;
      }
      body {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .flyer { box-shadow: none; border-radius: 0; }
    }
  </style>
</head>
<body>
  <div class="screen-hint">
    <button onclick="window.print()">Print Flyer</button>
    <span>Use Print &rarr; Save as PDF, or print directly to paper.</span>
  </div>
  <main class="flyer">
    <h1>${title}</h1>
    <div class="qr-wrap">
      <img src="${qrSrc}" alt="QR code to open the take-action page" width="520" height="520" />
    </div>
    <p class="scan-cta">Scan to take action</p>
    <p class="scan-sub">Opens this campaign so you can call with the script.</p>
    <div class="meta">
      ${bill ? `<div><h2>About the bill</h2><p>${bill}</p></div>` : ""}
      <div>
        <h2>Or open this link</h2>
        <p class="url">${url}</p>
      </div>
    </div>
    <div class="footer-wrap">
      ${org ? `<p class="footer-organizer">Organized by ${org}</p>` : ""}
      <p class="footer-brand">Powered by CivixThread</p>
    </div>
  </main>
  <script>
    (function () {
      var img = document.querySelector(".qr-wrap img");
      function triggerPrint() {
        setTimeout(function () { window.focus(); window.print(); }, 250);
      }
      if (img && !img.complete) {
        img.addEventListener("load", triggerPrint);
        img.addEventListener("error", triggerPrint);
      } else {
        triggerPrint();
      }
    })();
  </script>
</body>
</html>`;

  // Use a Blob URL instead of document.write to bypass strict pop-up blockers
  // and ensure the browser treats it as a proper document.
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);

  const printWindow = window.open(blobUrl, "_blank", "noopener,noreferrer");
  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    throw new CampaignPrintFlyerError(
      "Your browser blocked the print window. Allow pop-ups for this site and try again.",
      "popup_blocked"
    );
  }

  // Clean up the blob URL after giving the new tab time to load
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
}
